/**
 * Meta Ad Audit — formatting utilities.
 *
 * Ported 1:1 from the standalone index.html. Every tab uses these so
 * numbers render identically to the original audit tool.
 *
 * Rules:
 *   • null / undefined / NaN  → "—"
 *   • Currency in USD, locale en-US
 *   • "fK" = whole-number rounded dollars (no decimals)
 *   • "f$" = full-precision dollars (2 decimals)
 *   • "fX" = multiplier (2.34x)
 *   • "fP" = percentage (2 decimals, "12.34%")
 *   • "fV" = variance % with explicit +/- sign, integer, no decimals
 *   • "fDate" = DD MMM YYYY locale-formatted
 */
export const f$ = (v) => {
    if (v == null || !Number.isFinite(v))
        return '—';
    return `$${Number(v).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};
export const fK = (v) => {
    if (v == null || !Number.isFinite(v))
        return '—';
    return `$${Number(v).toLocaleString('en-US', {
        maximumFractionDigits: 0,
    })}`;
};
export const fX = (v) => {
    if (v == null || !Number.isFinite(v))
        return '—';
    return `${Number(v).toFixed(2)}x`;
};
export const fP = (v) => {
    if (v == null || !Number.isFinite(v))
        return '—';
    return `${Number(v).toFixed(2)}%`;
};
export const fV = (v) => {
    if (v == null || !Number.isFinite(v))
        return '';
    const sign = v > 0 ? '+' : '';
    return `${sign}${Math.round(v)}%`;
};
export const fDate = (s) => {
    if (!s)
        return '—';
    const d = typeof s === 'string' ? new Date(s) : s;
    if (isNaN(d.getTime()))
        return '—';
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};
export const resultColor = (r) => {
    switch (r) {
        case 'Pass':
            return '#027A48';
        case 'Optimize':
            // Amber — was '#B54708' (orange-brown) but rendered too close to
            // Fail's red on white backgrounds. #CA8A04 is yellow-brown and
            // clearly distinct from #B42318.
            return '#CA8A04';
        case 'Fail':
            return '#B42318';
        case 'No Benchmark':
            return '#5925DC';
        case 'Learning':
        default:
            return '#475467';
    }
};
export const resultBg = (r) => {
    switch (r) {
        case 'Pass':
            return '#ECFDF3';
        case 'Optimize':
            return '#FEF7C3';
        case 'Fail':
            return '#FEF3F2';
        case 'No Benchmark':
            return '#F4EBFF';
        case 'Learning':
        default:
            return '#F2F4F7';
    }
};
// Variance % used to classify AT-metrics — ported 1:1 from standalone
// (This is informational — ratings are already computed server-side.)
export const calcVariancePct = (actual, bench) => {
    if (actual == null || bench == null || bench === 0)
        return null;
    return ((actual - bench) / bench) * 100;
};
